import { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { getMerchantWebhooks, createMerchantWebhook, deleteMerchantWebhook, testMerchantWebhook } from '../../lib/api';
import { toast } from 'sonner';
import { Webhook, Plus, Trash2, PlayCircle, Loader2, Check, X, Globe } from 'lucide-react';
import { motion } from 'framer-motion';

const WEBHOOK_EVENTS = [
  { value: 'payment.completed', label: 'Payment Completed' },
  { value: 'payment.refunded', label: 'Payment Refunded' },
  { value: 'invoice.paid', label: 'Invoice Paid' },
  { value: 'invoice.overdue', label: 'Invoice Overdue' },
  { value: 'payout.completed', label: 'Payout Completed' }
];

const MerchantWebhooksTab = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [testing, setTesting] = useState(null);
  
  const [form, setForm] = useState({
    url: '',
    events: []
  });

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    try {
      const res = await getMerchantWebhooks();
      setWebhooks(res.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Failed to load webhooks');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleEvent = (event) => {
    if (form.events.includes(event)) {
      setForm({ ...form, events: form.events.filter(e => e !== event) });
    } else {
      setForm({ ...form, events: [...form.events, event] });
    }
  };

  const handleCreate = async () => {
    if (!form.url) {
      toast.error('Webhook URL is required');
      return;
    }
    if (form.events.length === 0) {
      toast.error('Select at least one event');
      return;
    }

    setCreating(true);
    try {
      await createMerchantWebhook(form);
      toast.success('Webhook created!');
      setShowCreate(false);
      setForm({ url: '', events: [] });
      loadWebhooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create webhook');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteMerchantWebhook(id);
      toast.success('Webhook deleted');
      loadWebhooks();
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const res = await testMerchantWebhook(id);
      if (res.data.success) {
        toast.success(`Test successful! Status: ${res.data.status_code}`);
      } else {
        toast.error(`Test failed: ${res.data.error || `Status ${res.data.status_code}`}`);
      }
    } catch (error) {
      toast.error('Failed to test webhook');
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-6" data-testid="webhooks-tab">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-heading font-semibold">Webhooks</h2>
          <p className="text-sm text-muted-foreground">Receive real-time notifications for payment events</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="rounded-xl" data-testid="create-webhook-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Webhook
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Create Webhook</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Endpoint URL *</Label>
                <Input
                  type="url"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://your-server.com/webhook"
                  className="rounded-xl font-mono text-sm"
                  data-testid="webhook-url"
                />
              </div>

              <div className="space-y-2">
                <Label>Events to Listen</Label>
                <div className="grid grid-cols-1 gap-2">
                  {WEBHOOK_EVENTS.map((event) => (
                    <button
                      key={event.value}
                      type="button"
                      onClick={() => toggleEvent(event.value)}
                      className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${
                        form.events.includes(event.value) 
                          ? 'border-emerald-500 bg-emerald-500/10' 
                          : 'border-border hover:border-emerald-500/50'
                      }`}
                    >
                      <span className="text-sm">{event.label}</span>
                      {form.events.includes(event.value) && (
                        <Check className="w-4 h-4 text-emerald-600" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleCreate} disabled={creating} className="w-full rounded-xl" data-testid="submit-webhook">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Webhook'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {webhooks.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Webhook className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No webhooks configured</p>
            <p className="text-sm text-muted-foreground">Add a webhook to receive payment notifications</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {webhooks.map((webhook, index) => (
            <motion.div
              key={webhook.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card data-testid={`webhook-${webhook.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Globe className="w-4 h-4 text-muted-foreground" />
                        <p className="font-mono text-sm truncate">{webhook.url}</p>
                        <Badge variant={webhook.is_active ? "default" : "secondary"} className="ml-auto shrink-0">
                          {webhook.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {webhook.events.map((event) => (
                          <Badge key={event} variant="outline" className="text-xs">
                            {event}
                          </Badge>
                        ))}
                      </div>
                      {webhook.last_triggered_at && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Last triggered: {new Date(webhook.last_triggered_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTest(webhook.id)}
                        disabled={testing === webhook.id}
                        className="rounded-lg"
                      >
                        {testing === webhook.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <PlayCircle className="w-4 h-4 mr-1" /> Test
                          </>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(webhook.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Webhook Secret Info */}
      {webhooks.length > 0 && (
        <Card className="bg-muted/50">
          <CardContent className="py-4">
            <p className="text-sm font-medium mb-2">Verifying Webhooks</p>
            <p className="text-xs text-muted-foreground">
              Each webhook includes an <code className="px-1 py-0.5 bg-muted rounded">X-ParachPay-Signature</code> header.
              Verify this signature using your webhook secret to ensure the request is authentic.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MerchantWebhooksTab;
